import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, Plus, Send, MessageSquare, Calendar, Mail, ArrowLeft, Crown, Video, Clock, Paperclip, X, BookOpen } from 'lucide-react';
import { CustomerSidebar } from '@/components/layout/CustomerSidebar';

const categories = [
  { value: 'hardware', label: 'Hardware Issue' },
  { value: 'software', label: 'Software Issue' },
  { value: 'network', label: 'Network/Connectivity' },
  { value: 'access', label: 'Access Request' },
  { value: 'email', label: 'Email/Communication' },
  { value: 'security', label: 'Security Concern' },
  { value: 'other', label: 'Other' },
];

interface Attachment { id: string; name: string; size: string; }
import type { AdminTicket } from '@sd/types/admin';
import type { ChatMessage, CustomerCompany, Meeting } from '@sd/types/customerCompany';
import ServiceDeskAPIService from '@sd/services/APIs';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { extractListArray } from '@sd/lib/formilyGridTicketCache';
import { mapFormilyToCustomerCompany } from '@sd/lib/customerCompanyFromFormily';
import {
  SERVICE_DESK_FORMILY_CUSTOMER_COMPANY_MODEL,
  serviceDeskFormilyCustomerCompanyGridQueryKey,
} from '@sd/services/APIs';
import { handleSubjectInputPaste } from '@/lib/pastePlainText';
import {
  clampTicketSubject,
  SUBJECT_MAX_LENGTH,
  subjectMaxLengthError,
} from '@sd/lib/ticketFormConstants';
import { toast } from 'sonner';
import {
  formatPriorityLabel,
  formatStatusLabel,
  formatTitleCase,
  priorityBucket,
} from '@sd/lib/ticketStreamUtils';

const priorityColors: Record<string, string> = {
  P1: 'bg-destructive text-destructive-foreground',
  P2: 'bg-orange-500 text-white',
  P3: 'bg-yellow-500 text-white',
  P4: 'bg-muted text-muted-foreground',
};
const statusColors: Record<string, string> = {
  open: 'bg-blue-500 text-white',
  pending: 'bg-yellow-500 text-white',
  resolved: 'bg-green-500 text-white',
  closed: 'bg-muted text-muted-foreground',
};

export default function CustomerPortal() {
  const { companyId } = useParams<{ companyId?: string }>();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { isEndUser } = useAuth();

  // ServiceDesk_User does not have access to this page — they have their own portal
  if (isEndUser) {
    return <Navigate to="/sd/my-portal" replace />;
  }

  const { data: gridPayload, isPending, isError } = useQuery({
    queryKey: serviceDeskFormilyCustomerCompanyGridQueryKey,
    queryFn: async () => {
      const data = await ServiceDeskAPIService.formily.getGridData(
        SERVICE_DESK_FORMILY_CUSTOMER_COMPANY_MODEL,
        1,
        200
      );
      if (data === undefined) {
        throw new Error("Failed to load companies");
      }
      return data;
    },
    staleTime: 30_000,
    retry: 1,
  });

  const customerCompanies = useMemo(() => {
    return extractListArray(gridPayload ?? null)
      .filter(
        (r): r is Record<string, unknown> =>
          r !== null && typeof r === "object" && !Array.isArray(r)
      )
      .map(r => mapFormilyToCustomerCompany(r));
  }, [gridPayload]);

  if (isPending) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <LoadingSpinner />
      </div>
    );
  }

  if (isError || customerCompanies.length === 0) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">No company profile available.</p>
      </div>
    );
  }

  const validRouteId =
    companyId !== undefined && customerCompanies.some(c => c.id === companyId);
  const defaultCompanyId = validRouteId ? companyId! : customerCompanies[0]!.id;

  return (
    <div className="h-screen flex bg-background overflow-hidden">
      <CustomerSidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        onNewTicket={() => window.dispatchEvent(new CustomEvent('customer:open-new-ticket'))}
      />
      <div className="flex-1 overflow-y-auto">
        <CustomerDashboard
          companyId={defaultCompanyId}
          customerCompanies={customerCompanies}
        />
      </div>
    </div>
  );
}

function CustomerDashboard({
  companyId,
  customerCompanies,
}: {
  companyId: string;
  customerCompanies: CustomerCompany[];
}) {
  const navigate = useNavigate();
  const company = customerCompanies.find(c => c.id === companyId);
  const [tickets, setTickets] = useState<AdminTicket[]>([]);
  const [activeTicketId, setActiveTicketId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [newMsg, setNewMsg] = useState('');
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: '', category: '', priority: 'P3', description: '' });
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  // Listen to sidebar "New Ticket" trigger
  useEffect(() => {
    const handler = () => setNewTicketOpen(true);
    window.addEventListener('customer:open-new-ticket', handler);
    return () => window.removeEventListener('customer:open-new-ticket', handler);
  }, []);

  const activeTicket = tickets.find(t => t.id === activeTicketId);
  const ticketMsgs = messages.filter(m => m.ticketId === activeTicketId).sort((a, b) => a.sentAt.localeCompare(b.sentAt));
  const ticketMtgs = meetings.filter(m => m.ticketId === activeTicketId);

  const send = () => {
    if (!newMsg.trim() || !activeTicketId) return;
    setMessages(prev => [...prev, {
      id: `msg-${Date.now()}`,
      ticketId: activeTicketId,
      sender: 'customer',
      senderName: company?.contactName || 'Customer',
      channel: 'chat',
      content: newMsg,
      sentAt: new Date().toISOString(),
      read: false,
    }]);
    setNewMsg('');
    toast.success('Message sent');
  };

  const handleAddAttachment = () => {
    setAttachments([...attachments, { id: Date.now().toString(), name: `screenshot_${attachments.length + 1}.png`, size: '245 KB' }]);
  };
  const handleRemoveAttachment = (id: string) => setAttachments(attachments.filter(a => a.id !== id));

  const submitTicket = () => {
    const subjectTrimmed = newTicket.subject.trim();
    if (!subjectTrimmed || !newTicket.category || !newTicket.description) {
      toast.error('Please fill all required fields');
      return;
    }
    if (subjectTrimmed.length > SUBJECT_MAX_LENGTH) {
      toast.error(subjectMaxLengthError());
      return;
    }
    const newId = `NEW-${Date.now().toString().slice(-5)}`;
    const created: AdminTicket = {
      id: newId,
      subject: newTicket.subject,
      requester: company?.contactName || 'You',
      requesterEmail: company?.registeredEmails[0] || '',
      department: 'Customer',
      assignedAgent: null,
      status: 'open',
      priority: newTicket.priority,
      category: newTicket.category,
      ticketType: 'request',
      slaStatus: 'on_track',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      channel: 'Portal',
      tags: [newTicket.category],
      escalationLevel: 0,
      notes: [],
    };
    setTickets(prev => [created, ...prev]);
    setActiveTicketId(newId);
    toast.success('Ticket submitted — admin will respond shortly');
    setNewTicket({ subject: '', category: '', priority: 'P3', description: '' });
    setAttachments([]);
    setNewTicketOpen(false);
  };

  const filteredTickets = tickets.filter(t => {
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    if (searchQuery && !t.subject.toLowerCase().includes(searchQuery.toLowerCase()) && !t.id.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const stats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    pending: tickets.filter(t => t.status === 'pending').length,
    resolved: tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length,
  };

  if (!company) return null;

  return (
    <div className="bg-background">
      <header className="border-b bg-card">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3 sm:px-6 sm:py-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-8 w-8 shrink-0 rounded-lg flex items-center justify-center text-primary-foreground font-bold sm:h-10 sm:w-10" style={{ backgroundColor: `hsl(${company.logoColor})` }}>{company.name.charAt(0)}</div>
            <div className="min-w-0">
              <h1 className="font-bold text-foreground truncate">{company.name}</h1>
              <p className="text-xs text-muted-foreground flex items-center gap-1">{company.supportPlan === 'paid' ? <><Crown className="h-3 w-3 shrink-0" /><span>Paid Support</span></> : 'Free Support'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Dialog open={newTicketOpen} onOpenChange={setNewTicketOpen}>
              <DialogTrigger asChild><Button size="sm" className="gap-1 sm:gap-2"><Plus className="h-4 w-4" /><span className="hidden sm:inline">New Ticket</span></Button></DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Submit New Ticket</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label htmlFor="ct-subject">Subject *</Label>
                    <Input
                      id="ct-subject"
                      placeholder="Brief description of the issue..."
                      value={newTicket.subject}
                      maxLength={SUBJECT_MAX_LENGTH}
                      onChange={e =>
                        setNewTicket({
                          ...newTicket,
                          subject: clampTicketSubject(e.target.value),
                        })
                      }
                      onPaste={e => handleSubjectInputPaste(e, SUBJECT_MAX_LENGTH)}
                    />
                    <p className="text-right text-xs text-muted-foreground">
                      {newTicket.subject.length} / {SUBJECT_MAX_LENGTH}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Category *</Label>
                      <Select value={newTicket.category} onValueChange={v => setNewTicket({ ...newTicket, category: v })}>
                        <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                        <SelectContent>
                          {categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Priority</Label>
                      <Select value={newTicket.priority} onValueChange={v => setNewTicket({ ...newTicket, priority: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="P1">P1 - Critical</SelectItem>
                          <SelectItem value="P2">P2 - High</SelectItem>
                          <SelectItem value="P3">P3 - Medium</SelectItem>
                          <SelectItem value="P4">P4 - Low</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ct-desc">Description *</Label>
                    <Textarea id="ct-desc" rows={5} placeholder="Provide detailed information about the issue..." value={newTicket.description} onChange={e => setNewTicket({ ...newTicket, description: e.target.value })} className="resize-none" />
                  </div>
                  <div className="space-y-2">
                    <Label>Attachments</Label>
                    <div className="space-y-2">
                      {attachments.map(a => (
                        <div key={a.id} className="flex items-center gap-3 p-2 rounded-md border bg-muted/30">
                          <Paperclip className="h-4 w-4 text-muted-foreground" />
                          <span className="flex-1 text-sm">{a.name}</span>
                          <span className="text-xs text-muted-foreground">{a.size}</span>
                          <button onClick={() => handleRemoveAttachment(a.id)} className="h-6 w-6 rounded hover:bg-muted flex items-center justify-center"><X className="h-4 w-4 text-muted-foreground" /></button>
                        </div>
                      ))}
                      <Button type="button" variant="outline" size="sm" className="gap-2" onClick={handleAddAttachment}>
                        <Paperclip className="h-4 w-4" />Add Attachment
                      </Button>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setNewTicketOpen(false)}>Cancel</Button>
                  <Button onClick={submitTicket}>Create Ticket</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4 space-y-3 sm:p-6 sm:space-y-4">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
          <Card><CardContent className="p-3 sm:p-4"><p className="text-xs text-muted-foreground">Total Tickets</p><p className="text-xl font-bold mt-0.5 sm:text-2xl sm:mt-1">{stats.total}</p></CardContent></Card>
          <Card><CardContent className="p-3 sm:p-4"><p className="text-xs text-muted-foreground">Open</p><p className="text-xl font-bold mt-0.5 text-primary sm:text-2xl sm:mt-1">{stats.open}</p></CardContent></Card>
          <Card><CardContent className="p-3 sm:p-4"><p className="text-xs text-muted-foreground">Pending</p><p className="text-xl font-bold mt-0.5 text-warning sm:text-2xl sm:mt-1">{stats.pending}</p></CardContent></Card>
          <Card><CardContent className="p-3 sm:p-4"><p className="text-xs text-muted-foreground">Resolved</p><p className="text-xl font-bold mt-0.5 text-success sm:text-2xl sm:mt-1">{stats.resolved}</p></CardContent></Card>
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-12 lg:gap-4">
        <Card className="flex flex-col max-h-[50vh] lg:col-span-4 lg:max-h-[calc(100vh-280px)]">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between mb-3">
              <CardTitle className="text-sm">My Tickets ({filteredTickets.length})</CardTitle>
              <Button size="sm" className="h-7 gap-1" onClick={() => setNewTicketOpen(true)}>
                <Plus className="h-3.5 w-3.5" />New
              </Button>
            </div>
            <div className="space-y-2">
              <Input placeholder="Search tickets..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="h-8 text-sm" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="space-y-1 p-2 overflow-y-auto flex-1">
            {filteredTickets.length === 0 && (
              <div className="p-6 text-center space-y-3">
                <div className="h-12 w-12 rounded-full bg-muted mx-auto flex items-center justify-center"><Plus className="h-6 w-6 text-muted-foreground" /></div>
                <p className="text-sm text-muted-foreground">{tickets.length === 0 ? 'No tickets yet. Create your first one!' : 'No tickets match your filters.'}</p>
                {tickets.length === 0 && <Button size="sm" onClick={() => setNewTicketOpen(true)}><Plus className="h-4 w-4 mr-1" />Create Ticket</Button>}
              </div>
            )}
            {filteredTickets.map(t => (
              <button key={t.id} onClick={() => setActiveTicketId(t.id)} className={`w-full text-left p-3 rounded-md hover:bg-muted ${activeTicketId === t.id ? 'bg-muted' : ''}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-muted-foreground">{t.id}</span>
                  <Badge className={priorityColors[priorityBucket(t.priority)]} variant="secondary">{formatPriorityLabel(t.priority)}</Badge>
                </div>
                <p className="text-sm font-medium mt-1 line-clamp-2">{t.subject}</p>
                <div className="flex items-center justify-between mt-2">
                  <Badge className={`${statusColors[t.status]} text-xs`} variant="secondary">{formatStatusLabel(t.status)}</Badge>
                  <span className="text-[10px] text-muted-foreground">{new Date(t.createdAt).toLocaleDateString()}</span>
                </div>
              </button>
            ))}
          </CardContent>
        </Card>

        <Card className="flex flex-col max-h-[calc(100vh-280px)] lg:col-span-8">
          {!activeTicket ? (
            <CardContent className="p-12 text-center text-muted-foreground flex-1 flex flex-col items-center justify-center gap-3">
              <MessageSquare className="h-12 w-12 opacity-30" />
              <p>Select a ticket to view conversation</p>
              <Button variant="outline" size="sm" onClick={() => setNewTicketOpen(true)}><Plus className="h-4 w-4 mr-1" />Or create a new ticket</Button>
            </CardContent>
          ) : (
            <>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2 min-w-0">
                  <div className="min-w-0">
                    <CardTitle className="break-words text-base sm:text-lg">{activeTicket.subject}</CardTitle>
                    <div className="flex flex-wrap items-center gap-1.5 mt-2 sm:gap-2">
                      <Badge className={statusColors[activeTicket.status]} variant="secondary">{formatStatusLabel(activeTicket.status)}</Badge>
                      <Badge className={priorityColors[priorityBucket(activeTicket.priority)]} variant="secondary">{formatPriorityLabel(activeTicket.priority)}</Badge>
                      <span className="text-xs text-muted-foreground">{new Date(activeTicket.createdAt).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="chat">
                  <TabsList>
                    <TabsTrigger value="chat"><MessageSquare className="h-4 w-4 mr-1" />Chat ({ticketMsgs.length})</TabsTrigger>
                    <TabsTrigger value="meetings"><Calendar className="h-4 w-4 mr-1" />Meetings ({ticketMtgs.length})</TabsTrigger>
                  </TabsList>
                  <TabsContent value="chat" className="space-y-3 mt-4">
                    <div className="border rounded-lg p-3 h-[400px] overflow-y-auto space-y-3 bg-muted/20">
                      {ticketMsgs.length === 0 && <p className="text-center text-sm text-muted-foreground py-12">Start the conversation</p>}
                      {ticketMsgs.map(m => (
                        <div key={m.id} className={`flex ${m.sender === 'customer' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[70%] rounded-lg p-3 ${m.sender === 'customer' ? 'bg-primary text-primary-foreground' : 'bg-card border'}`}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium">{m.senderName}</span>
                              <Badge variant="outline" className={`text-xs h-4 ${m.sender === 'customer' ? 'border-primary-foreground/30 text-primary-foreground' : ''}`}>
                                {m.channel === 'email' ? <Mail className="h-2.5 w-2.5 mr-0.5" /> : <MessageSquare className="h-2.5 w-2.5 mr-0.5" />}{m.channel}
                              </Badge>
                            </div>
                            <p className="text-sm">{m.content}</p>
                            <p className={`text-[10px] mt-1 ${m.sender === 'customer' ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>{new Date(m.sentAt).toLocaleString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input value={newMsg} onChange={e => setNewMsg(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()} placeholder="Type a message..." />
                      <Button onClick={send}><Send className="h-4 w-4" /></Button>
                    </div>
                  </TabsContent>
                  <TabsContent value="meetings" className="space-y-3 mt-4">
                    {ticketMtgs.length === 0 && <p className="text-center text-sm text-muted-foreground py-12">No meetings scheduled</p>}
                    {ticketMtgs.map(m => (
                      <div key={m.id} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold flex items-center gap-2"><Video className="h-4 w-4 text-primary" />{m.title}</h4>
                            <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(m.scheduledFor).toLocaleString()} · {m.durationMinutes} min</p>
                            {m.notes && <p className="text-sm mt-2">{m.notes}</p>}
                            {m.link && <a href={m.link} className="text-xs text-primary mt-2 inline-block" target="_blank" rel="noreferrer">Join: {m.link}</a>}
                          </div>
                          <Badge variant={m.status === 'scheduled' ? 'default' : 'secondary'}>{formatTitleCase(m.status)}</Badge>
                        </div>
                      </div>
                    ))}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </>
          )}
        </Card>
        </div>
      </div>
    </div>
  );
}
